import { DashboardReports } from "../../components/skins/dashboard-reports.ts";
import { globalState } from "../../framework/globalState.ts";
import { fetchKpiSummary, fetchKpiEnergyMix, exportAuditLogs } from "../../services/api.ts";

export default function ReportsPage() {
  const container = document.createElement("div");
  container.classList.add("parent-container");

  const siteWithData = globalState.currentSiteWithData;
  const site = (siteWithData as any)?.site || siteWithData;
  const siteId = site?._id;

  container.innerHTML = `
    <div class="container-layout full-height">
      <div class="container-column">
        <div class="column-header">
          <div class="header-title has-icon">
            <span class="iconify" data-icon="mdi:chart-box-outline"></span>
            <h3>Reports – ${site?.name || 'Site'}</h3>
          </div>
          <div class="bar-row d-flex align-items-center gap-10">
            <select id="report-days" class="form-select" style="width:auto;">
              <option value="7">Last 7 days</option>
              <option value="30" selected>Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
            <button id="export-btn" class="btn btn-sm"><span class="iconify" data-icon="flowbite:download-solid"></span> Export</button>
          </div>
        </div>

        <div class="container-body" style="padding:16px; overflow-y:auto;">
          <div id="kpi-cards" class="d-flex flex-wrap gap-10" style="margin-bottom:20px;"></div>
          <div id="energy-mix-section">
            <h4 style="margin:16px 0 8px;">Energy Mix</h4>
            <div class="table-responsive">
              <table class="has-min-width border-table">
                <thead><tr><th>Month</th><th>Solar (kWh)</th><th>Battery (kWh)</th><th>Grid (kWh)</th><th>Generator (kWh)</th></tr></thead>
                <tbody id="energy-mix-body"><tr><td colspan="5" class="text-center">Loading...</td></tr></tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  const kpiCards = container.querySelector('#kpi-cards')!;
  const energyMixBody = container.querySelector('#energy-mix-body')!;
  const daysSelect = container.querySelector('#report-days') as HTMLSelectElement;
  const exportBtn = container.querySelector('#export-btn');

  function renderKpiCard(label: string, value: string | number, unit: string) {
    return `<div class="bg-subcontainer" style="padding:12px 18px; border-radius:8px; min-width:160px; flex:1;">
      <div style="font-size:12px; opacity:0.7;">${label}</div>
      <div style="font-size:22px; font-weight:600;">${value}<span style="font-size:13px; opacity:0.6; margin-left:4px;">${unit}</span></div>
    </div>`;
  }

  async function loadReport(days: number) {
    if (!siteId) return;

    kpiCards.innerHTML = '<div style="opacity:0.5;">Loading KPIs...</div>';
    energyMixBody.innerHTML = `<tr><td colspan="5" class="text-center">Loading...</td></tr>`;

    try {
      const [summary, energyMix]: [any, any] = await Promise.all([
        fetchKpiSummary(siteId, days),
        fetchKpiEnergyMix(siteId, Math.ceil(days / 30) || 1)
      ]);

      const avg = summary?.averages || summary?.data?.averages || {};
      kpiCards.innerHTML = [
        renderKpiCard('Performance Ratio', (avg.performanceRatio ?? 0).toFixed(1), '%'),
        renderKpiCard('Specific Yield', (avg.specificYield ?? 0).toFixed(2), 'kWh/kWp'),
        renderKpiCard('Total Energy', (avg.totalEnergy ?? 0).toFixed(0), 'kWh'),
        renderKpiCard('Battery SOC', (avg.batterySOC ?? 0).toFixed(1), '%'),
        renderKpiCard('CO₂ Savings', (avg.co2Savings ?? 0).toFixed(1), 'kg'),
        renderKpiCard('Plant Efficiency', (avg.plantEfficiency ?? 0).toFixed(1), '%'),
        renderKpiCard('Grid Import', (avg.gridImport ?? 0).toFixed(0), 'kWh'),
        renderKpiCard('Grid Export', (avg.gridExport ?? 0).toFixed(0), 'kWh'),
      ].join('');

      const mixRows = Array.isArray(energyMix) ? energyMix : energyMix?.data || [];
      if (mixRows.length === 0) {
        energyMixBody.innerHTML = `<tr><td colspan="5" class="text-center">No energy mix data</td></tr>`;
      } else {
        energyMixBody.innerHTML = mixRows.map((row: any) => `<tr>
          <td>${row.month || row.period || '-'}</td>
          <td class="text-right">${(row.solar ?? row.renewableEnergy ?? 0).toFixed(0)}</td>
          <td class="text-right">${(row.battery ?? 0).toFixed(0)}</td>
          <td class="text-right">${(row.grid ?? row.gridImport ?? 0).toFixed(0)}</td>
          <td class="text-right">${(row.generator ?? row.generatorEnergy ?? 0).toFixed(0)}</td>
        </tr>`).join('');
      }
    } catch (err) {
      console.error('[Reports] Load error:', err);
      kpiCards.innerHTML = '<div class="text-danger">Error loading KPI data</div>';
      energyMixBody.innerHTML = `<tr><td colspan="5" class="text-center text-danger">Error</td></tr>`;
    }
  }

  daysSelect?.addEventListener('change', () => loadReport(Number(daysSelect.value)));

  exportBtn?.addEventListener('click', async () => {
    try {
      if (siteId) await exportAuditLogs({ siteId });
    } catch (err) {
      console.error('[Reports] Export error:', err);
    }
  });

  if (siteId) {
    loadReport(30);
  } else {
    kpiCards.innerHTML = '<div class="text-warning">Select a site to view reports.</div>';
  }

  return container;
}
